import React, {FC, useEffect, useState} from "react"

import {CompanyNewsTile, CompanyNewsTileConfig, FileStatus} from "@irati/types"
import cultureHubTracking from "@irati-tracking/homepage/cultureHub"
import Flex from "@irati/ui/src/Flex"
import TabPills from "@irati/ui/src/TabPills"
import {Body, Label} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Spacing from "@irati/ui/src/Spacing"
import authentication from "@irati/auth"
import {useDeleteCustomerFile} from "@irati/customer-files/hooks/customerFile"
import {Modal} from "../../Modal"
import {useCultureHubStore} from "../../../stores/hooks"
import {getType} from "../utils/type"
import {SelectedTab} from "./types"
import ContentForm, {TypeAction} from "./Form"

interface Props {
  companyNewsTile: CompanyNewsTile
  onClose: () => void
  isEditMode?: boolean
}

const CompanyNewsContentModal: FC<Props> = ({companyNewsTile, onClose, isEditMode = false}) => {
  const {state, dispatch} = useCultureHubStore()
  const [updatedContent, setUpdatedContent] = useState<CompanyNewsTileConfig>(companyNewsTile.config)
  const [deleteFileMutation] = useDeleteCustomerFile()

  const newContentObject = Object.assign({}, updatedContent, {
    type: getType(state.currentTab, companyNewsTile)
  })

  const updateStateContent = (c: CompanyNewsTileConfig) => {
    setUpdatedContent(c)
  }

  useEffect(() => {
    if (isEditMode) {
      dispatch({
        type: "SET_CURRENT_TAB",
        payload: {
          currentTab: SelectedTab.EDIT
        }
      })
    }
  }, [isEditMode])

  const handleClose = () => {
    if (!state.modalState) {
      if (state.isPooling) {
        return
      } else {
        if (state.uploadStatus) {
          updateStateContent(Object.assign({}, newContentObject, {fileId: state.prevFileId}))
          deleteFileMutation({
            variables: {uuid: newContentObject.fileId}
          })
        }
      }
      dispatch({type: "RESET"})
      onClose()
    } else {
      if (state.modalState === "AcceptTerms") {
        dispatch({type: "ACCEPTED_FILE", payload: {acceptedFile: undefined}})
        if (state.uploadStatus !== FileStatus.StatusReady) {
          dispatch({type: "ACCEPTED_FILE_VISUAL", payload: {acceptedFileVisual: undefined}})
        }
        dispatch({
          type: "SET_MODAL_STATE",
          payload: {modalState: undefined}
        })
      } else {
        if (state.modalState !== "ConfirmationScreen" && state.modalState !== "PickFromLibrary") {
          dispatch({
            type: "SET_MODAL_STATE",
            payload: {modalState: undefined}
          })
        } else {
          dispatch({
            type: "SET_MODAL_STATE",
            payload: {
              modalState: undefined
            }
          })
          dispatch({
            type: "SET_NOTIFICATION",
            payload: {
              isNotificationChecked: false
            }
          })
          cultureHubTracking.clickCultureHubCta("culturehub", "notify_all_cancel")
        }
      }
    }
  }

  const tabs = [
    {
      pill: (
        <Flex gap={0.5} alignItems="center" paddingLeft={5} paddingRight={5}>
          <Flex direction="row" gap={0.5}>
            <Label>🔗️</Label>
            <Label>
              <FormattedMessage id="platform.homepage.companyNews.newContent.url.title" />
            </Label>
          </Flex>
        </Flex>
      ),
      onClick: () =>
        !state.isPreview &&
        dispatch({
          type: "SET_CURRENT_TAB",
          payload: {
            currentTab: SelectedTab.IMAGE
          }
        }),
      isTabActive: state.currentTab === SelectedTab.IMAGE && true
    },
    {
      pill: (
        <Flex gap={0.5} alignItems="center" paddingLeft={5} paddingRight={5}>
          <Flex direction="row" gap={0.5}>
            <Label>🎥</Label>
            <Label>
              <FormattedMessage id="platform.homepage.companyNews.newContent.video.title" />
            </Label>
          </Flex>
        </Flex>
      ),
      onClick: () =>
        !state.isPreview &&
        dispatch({
          type: "SET_CURRENT_TAB",
          payload: {
            currentTab: SelectedTab.VIDEO
          }
        }),
      isTabActive: state.currentTab === SelectedTab.VIDEO && true
    }
  ]

  if (!authentication.apps("platform.cultureHubFileUploadHidden")) {
    tabs.push({
      pill: (
        <Flex gap={0.5} alignItems="center" paddingLeft={5} paddingRight={5}>
          <Flex direction="row" gap={0.5}>
            <Label>📁</Label>
            <Label>
              <FormattedMessage id="culturehub.companyNews.newContent.tabs.upload.title" />
            </Label>
          </Flex>
        </Flex>
      ),
      onClick: () =>
        !state.isPreview &&
        dispatch({
          type: "SET_CURRENT_TAB",
          payload: {
            currentTab: SelectedTab.UPLOAD
          }
        }),
      isTabActive: state.currentTab === SelectedTab.UPLOAD && true
    })
  }

  return (
    <Modal
      hideHeader={!!state.modalState}
      header={
        isEditMode ? (
          <Body>
            <FormattedMessage id="platform.homepage.companyNews.editContent.title" />
          </Body>
        ) : (
          <Body>
            <FormattedMessage id="platform.homepage.companyNews.createContent.title" />
          </Body>
        )
      }
      onClose={handleClose}>
      {!isEditMode &&
        state.modalState !== "PickFromLibrary" &&
        state.modalState !== "ConfirmationScreen" &&
        state.modalState !== "AcceptTerms" && (
          <Spacing marginTop={2}>
            <TabPills tabs={tabs} />
          </Spacing>
        )}
      <ContentForm
        companyNewsTile={companyNewsTile}
        currentTab={state.currentTab}
        onClose={onClose}
        typeAction={isEditMode ? TypeAction.Edit : TypeAction.Create}
        updateContent={updateStateContent}
        companyNewsTileConfig={newContentObject}
      />
    </Modal>
  )
}

export default CompanyNewsContentModal
